import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';
import { WifiOff } from 'lucide-react';
import { formatDate, formatTime } from '@/lib/format';
import { useOnline } from '@/lib/hooks';
import { useI18n } from '@/lib/i18n';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Logo } from './Logo';
import { Spinner } from '@/components/ui/Spinner';

/** Calls `onIdle` after `ms` without pointer, key or touch activity (kiosk session reset). */
export function useIdleTimer(onIdle: () => void, ms = 60_000, enabled = true): void {
  const saved = useRef(onIdle);
  useEffect(() => {
    saved.current = onIdle;
  });
  useEffect(() => {
    if (!enabled) {
      return;
    }
    let timer = window.setTimeout(() => saved.current(), ms);
    const reset = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => saved.current(), ms);
    };
    const events = ['pointerdown', 'keydown', 'touchstart'] as const;
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => {
      window.clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [ms, enabled]);
}

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <div className="text-right leading-tight" aria-hidden="true">
      <p className="tabular font-display text-2xl font-semibold text-white">{formatTime(now)}</p>
      <p className="text-xs text-navy-300">{formatDate(now)}</p>
    </div>
  );
}

/**
 * Fullscreen self-checkout frame: no navigation, large type, navy canvas, clock and language switch.
 * Child routes decide the steps (scan card, scan books, receipt).
 */
export function KioskShell() {
  const { t } = useI18n();
  const online = useOnline();
  return (
    <div className="theme-dark relative flex min-h-dvh flex-col overflow-hidden bg-midnight text-white">
      <div className="pointer-events-none absolute -left-40 -top-40 h-[32rem] w-[32rem] rounded-full bg-sapphire-500/25 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-48 -right-32 h-[30rem] w-[30rem] rounded-full bg-ice-500/15 blur-3xl" aria-hidden="true" />
      <header className="relative flex items-center justify-between px-8 py-6">
        <Logo size={48} tone="onDark" subtitle={t('kiosk.title')} />
        <div className="flex items-center gap-6">
          <div className="rounded-xl bg-white/10 ring-1 ring-white/10 [&_button]:text-white [&_button:hover]:bg-white/10">
            <LanguageSwitcher compact />
          </div>
          <Clock />
        </div>
      </header>
      {!online && (
        <div role="status" className="relative mx-8 flex items-center gap-2 rounded-xl bg-warning-500/20 px-4 py-3 text-warning-500">
          <WifiOff className="h-5 w-5" aria-hidden="true" />
          {t('shell.offline')}
        </div>
      )}
      <main id="main" tabIndex={-1} className="relative flex flex-1 flex-col px-8 pb-10 outline-none">
        <Suspense fallback={<div className="grid flex-1 place-items-center"><Spinner size="lg" className="text-sapphire-300" label={t('common.loading')} /></div>}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
